import { useEffect, useRef, useState } from "react";

import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import { UPLOAD_ENDPOINT } from "@constants/endpoints";
import {
    DEFAULT_MAX_SIZE_MB,
    PREVIEW_KIND,
    UPLOAD_FAILED_MESSAGE,
    UPLOAD_STATUS
} from "./constants";

const BYTES_IN_MB = 1024 * 1024;

let uid = 0;
const nextId = () => {
    uid += 1;
    return `upload-${uid}`;
};

const previewKindOf = (file) => {
    if (file.type?.startsWith("image/")) return PREVIEW_KIND.IMAGE;
    if (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf")) return PREVIEW_KIND.PDF;
    return PREVIEW_KIND.FILE;
};

/**
 * Everything behind <FileUploader />: the hidden input, drag highlighting,
 * client-side validation, the staged list with its previews, and the upload
 * itself with per-file progress.
 *
 * Staging is deliberately not uploading. A picked file is held locally, shown
 * back to the user, and only sent when `upload()` is called — so nothing
 * reaches storage, and `onUploaded` never fires, without an explicit confirm.
 * @param options
 */
const useFileUploader = ({
    uploadType,
    multiple = true,
    mimeTypes = [],
    extensions = [],
    maxSizeMb = DEFAULT_MAX_SIZE_MB,
    onUploaded,
    disabled = false,
    requireName = false
}) => {
    // The uploads service answers with its own errors; they are reported per
    // file in the list instead of as a toast per rejected file.
    const { axiosInstance } = useAxios({ base: "base", showAlert: false });
    const inputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [items, setItems] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Object URLs are only freed on unmount here; removal frees its own, and a
    // ref keeps this cleanup from capturing an empty first render.
    const itemsRef = useRef(items);
    itemsRef.current = items;
    useEffect(() => () => {
        itemsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    }, []);

    const typeNote = extensions.map((ext) => ext.replace(".", "").toUpperCase()).join(", ");

    const validate = (file) => {
        const name = file.name?.toLowerCase() || "";
        const isSupported = mimeTypes.length === 0 && extensions.length === 0
            ? true
            : mimeTypes.includes(file.type) || extensions.some((ext) => name.endsWith(ext));

        if (!isSupported) return `${file.name} was skipped — only ${typeNote} files are allowed.`;
        if (file.size > maxSizeMb * BYTES_IN_MB) return `${file.name} was skipped — the limit is ${maxSizeMb} MB.`;

        return null;
    };

    const patchItem = (id, changes) => setItems((current) => current.map(
        (item) => (item.id === id ? { ...item, ...changes } : item)
    ));

    const stage = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;

        const accepted = [];
        const rejected = [];

        files.forEach((file) => {
            const error = validate(file);
            if (error) rejected.push(error);
            else accepted.push(file);
        });

        if (rejected.length > 0) toastify({ title: rejected.join(" "), type: "warning" });
        if (accepted.length === 0) return;

        const staged = (multiple ? accepted : accepted.slice(0, 1)).map((file) => {
            const kind = previewKindOf(file);
            return {
                id        : nextId(),
                file,
                name      : file.name,
                size      : file.size,
                kind,
                // Only an image is rendered from the blob; anything else would
                // hold an object URL open for a preview it never shows.
                previewUrl: kind === PREVIEW_KIND.IMAGE ? URL.createObjectURL(file) : "",
                status    : UPLOAD_STATUS.STAGED,
                error     : "",
                // What the user calls the file, asked for when `requireName` is
                // set. Starts empty so it is a deliberate answer, not a default.
                label     : ""
            };
        });

        setItems((current) => {
            if (!multiple) {
                current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
                return staged;
            }
            return [...current, ...staged];
        });
    };

    const removeItem = (id) => setItems((current) => current.filter((item) => {
        if (item.id !== id) return true;
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        return false;
    }));

    const setItemLabel = (id, label) => patchItem(id, { label });

    const pendingItems = items.filter((item) => item.status !== UPLOAD_STATUS.DONE);
    const isMissingName = requireName && pendingItems.some((item) => !item.label.trim());

    const clear = () => setItems((current) => {
        current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
        return [];
    });

    const openPicker = () => {
        if (disabled || uploading) return;
        inputRef.current?.click();
    };

    const sendOne = async (item) => {
        patchItem(item.id, { status: UPLOAD_STATUS.UPLOADING, error: "" });

        try {
            const formData = new FormData();
            formData.append("file", item.file);

            const res = await axiosInstance.post(UPLOAD_ENDPOINT, formData, { params: { type: uploadType } });

            const uploaded = (res.data || [])[0];
            if (!uploaded?.url) throw new Error(UPLOAD_FAILED_MESSAGE);

            patchItem(item.id, { status: UPLOAD_STATUS.DONE });

            return {
                url         : uploaded.url,
                // The uploader answers with `path`/`originalName`; fall all the
                // way back to the picked file rather than handing on a null.
                name        : uploaded.name || uploaded.path || uploaded.originalName || item.name,
                originalName: uploaded.originalName || item.name,
                label       : item.label.trim(),
                size        : uploaded.size ?? item.size
            };
        } catch (error) {
            patchItem(item.id, {
                status: UPLOAD_STATUS.ERROR,
                error : error.response?.data?.message || UPLOAD_FAILED_MESSAGE
            });
            return null;
        }
    };

    // Sequential on purpose: files upload as a queue being worked through, and
    // the uploads service is not asked to take a whole folder at once.
    const upload = async () => {
        const pending = pendingItems;
        if (pending.length === 0 || uploading || isMissingName) return;

        setUploading(true);

        const results = [];
        for (const item of pending) {
            results.push(await sendOne(item));
        }

        const uploaded = results.filter(Boolean);
        const failedCount = results.length - uploaded.length;

        if (uploaded.length > 0) {
            try {
                await onUploaded?.(uploaded);
                // Only what is now attached leaves the list; a failed file stays
                // staged so it can be retried without being picked again.
                setItems((current) => current.filter((item) => {
                    if (item.status !== UPLOAD_STATUS.DONE) return true;
                    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                    return false;
                }));
            } catch {
                // The attach step reports its own failure; the files stay listed
                // as uploaded so the retry does not re-send them.
            }
        }

        if (failedCount > 0) {
            toastify({ title: `${failedCount} file${failedCount > 1 ? "s" : ""} failed to upload.`, type: "error" });
        }

        setUploading(false);
    };

    const inputProps = {
        ref     : inputRef,
        type    : "file",
        multiple,
        onChange: (e) => {
            stage(e.target.files);
            // Reset so picking the same filename again still fires onChange.
            e.target.value = "";
        }
    };

    const dropProps = {
        onDragOver: (e) => {
            if (disabled || uploading) return;
            e.preventDefault();
            setDragActive(true);
        },
        onDragLeave: () => setDragActive(false),
        onDrop     : (e) => {
            e.preventDefault();
            setDragActive(false);
            if (disabled || uploading) return;
            stage(e.dataTransfer.files);
        }
    };

    return {
        items,
        uploading,
        dragActive,
        hasStaged: items.length > 0,
        isMissingName,
        openPicker,
        removeItem,
        setItemLabel,
        clear,
        upload,
        inputProps,
        dropProps
    };
};

export default useFileUploader;
